import { Instance, types } from 'mobx-state-tree';
import { DateTime } from 'luxon';
import { OrderEventLogDtoModelBase } from './OrderEventLogDtoModel.base';
import { OrderCompletionStatus } from './OrderCompletionStatusEnum';
import { formatDisplayDateTime, LOCATION_EXCEPTION_DISTANCE_THRESHOLD_METERS } from '../../../constants';
import getAppRoot from '../utils/getAppRoot';

/* The TypeScript type of an instance of OrderEventLogDtoModel */
export interface OrderEventLogDtoModelType extends Instance<typeof OrderEventLogDtoModel.Type> {}

/* A graphql query fragment builders for OrderEventLogDtoModel */
export { selectFromOrderEventLogDto, orderEventLogDtoModelPrimitives, OrderEventLogDtoModelSelector } from './OrderEventLogDtoModel.base';

/**
 * OrderEventLogDtoModel
 *
 * Override orderUuid from types.identifier to types.string.
 * The base model uses types.identifier which requires a non-null string/number,
 * but order event logs can have null/undefined orderUuid values from the API.
 * Using types.identifier also causes MST to register these in its identifier
 * cache, which is unnecessary since we never resolve OrderEventLogDto by identifier.
 * Fixes Sentry PLANNING-WEB-AK4.
 */
export const OrderEventLogDtoModel = OrderEventLogDtoModelBase
  .props({
    orderUuid: types.string,
  })
  .views(self => ({
    get completionLocation(): { id: string; latitude: number; longitude: number } | undefined {
      if (self.latitude != null && self.longitude != null) {
        return {
          id: self.eventUuid as string,
          latitude: self.latitude,
          longitude: self.longitude,
        };
      }
      return undefined;
    },
    /**
     * True when this completion (delivered/missed) was recorded further than
     * the allowed threshold from the order's planned location.
     */
    get hasLocationException(): boolean {
      const isCompletion =
        self.status === OrderCompletionStatus.DELIVERED ||
        self.status === OrderCompletionStatus.MISSED;
      return (
        isCompletion &&
        self.distanceFromOrder != null &&
        self.distanceFromOrder > LOCATION_EXCEPTION_DISTANCE_THRESHOLD_METERS
      );
    },
    get formattedDateTime(): string | undefined {
      if (!self.date) return undefined;
      
      const parsed = DateTime.fromISO(self.date, { setZone: true });
      if (!parsed.isValid) return undefined;
      
      const dateTimeFormat = getAppRoot(self).organization?.settings?.dateTimeFormat;
      return formatDisplayDateTime(parsed, dateTimeFormat);
    },
  }))
  .actions(self => ({
    // This is an auto-generated example action.
    log() {
      console.log(JSON.stringify(self));
    },
  }));
